/** packages/mouse-management/src/pages/performance/ReportRateCard.tsx */
import React from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabItem, useMessage } from "@eweadn-qiankun/shared-components";
import { trackClick } from "@eweadn-qiankun/shared-utils";
import "./ReportRateCard.scss";

interface ReportRateOption {
  value: number;
  rate: number;
  option: string;
}

interface ReportRateCardProps {
  reportRate: number;
  reportRates: ReportRateOption[];
  onReportRateChange: (index: number) => void;
  /** 有值时：高于该 Hz 的档位禁用点击（仍展示完整分段） */
  maxReportRateHz?: number;
  /**
   * 固定轮询率（Hz）：仅该档位保持选中样式且可点击；再次点击提示已是该档位。
   * 用于富芮坤有线等不支持切换回报率（实际固定 1K）。
   */
  fixedReportRateHz?: number;
  /**
   * 交互锁定：开启 20K FPS 等场景下禁止切换轮询率（仍展示当前选中态）
   */
  interactionLocked?: boolean;
}

const ReportRateCard: React.FC<ReportRateCardProps> = ({
  reportRate,
  reportRates,
  onReportRateChange,
  maxReportRateHz,
  fixedReportRateHz,
  interactionLocked = false,
}) => {
  const { t } = useTranslation();
  const toast = useMessage();

  const tabDisabled = (rateHz: number) => {
    if (interactionLocked) return true;
    if (typeof fixedReportRateHz === "number") {
      return rateHz !== fixedReportRateHz;
    }
    return (
      typeof maxReportRateHz === "number" &&
      maxReportRateHz > 0 &&
      rateHz > maxReportRateHz
    );
  };

  const handleTabChange = (key: string) => {
    const index = Number(key);
    const opt = reportRates[index];
    if (
      typeof fixedReportRateHz === "number" &&
      opt?.rate === fixedReportRateHz
    ) {
      toast.info(t("mouse.performance.reportRateAlreadyActive"));
      return;
    }
    onReportRateChange(index);
    trackClick({
      moduleId: "mouse-management/performance",
      elementId: "performance/report-rate",
      appType: "mouse",
      deviceType: "mouse",
      extra: { rateHz: opt?.rate, index },
    });
  };

  return (
    <div className="report-rate-card">
      <div className="card-header">
        <h3 className="card-title">{t("mouse.performance.reportRate")}</h3>
        <p className="rate-description">
          {t("mouse.performance.reportRateDesc")}
        </p>
      </div>
      <div className="report-rate-slider">
        <Tabs
          activeKey={String(reportRate)}
          onChange={handleTabChange}
          onDisabledTabClick={() =>
            toast.info(
              t(
                interactionLocked
                  ? "mouse.performance.fps20kInteractionLocked"
                  : "mouse.performance.reportRateNotSupported"
              )
            )
          }
          className={`report-rate-tabs report-rate-tabs--${reportRate}`}
          height={40}
          outerBorderRadius={100}
          innerBorderRadius={100}
          sliderClassName="gradient-border-animation"
        >
          {reportRates.map((rate, index) => (
            <TabItem
              key={index}
              tabKey={String(index)}
              disabled={tabDisabled(rate.rate)}
              label={
                rate.rate >= 1000 ? `${rate.rate / 1000}K` : String(rate.rate)
              }
            />
          ))}
        </Tabs>
        <div className="slider-labels">
          <span className="label-left">
            {t("mouse.performance.dailyOffice")}
          </span>
          <span className="label-right">
            {t("mouse.performance.gamingEsports")}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReportRateCard;
